/**
 * SelectionsWalkthrough — present the issued Selections Package to the client one item at a time and
 * record their answer as they give it (Matt, 2026-10-26). Full screen, for a shared screen or the room.
 *
 * Who answered is the CLIENT (picked from the people on the project's Client Hub); who recorded it is
 * the team member at the keyboard. Anyone on the team may record. The answers are the same rows the
 * Client Hub card reads, so the card fills in at the same time; anything not answered here the client
 * finishes on their own. "Write into the minutes" puts the meeting's answers in that meeting's minutes,
 * so the client can correct the record by the usual revision policy.
 *
 * Opens on `open-selections-walkthrough` { msId }. Mounted once in OutlineView.
 */

import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { loadSelectionsReview, recordSelectionAnswer, writeSelectionsToMinutes } from '../services/selectionsReview.js'
import { ctx } from '../outline/ctx.js'

const INK = '#07272D', MUTED = '#7a9aaa'
const CHOICES = [
  { status: 'approved', label: 'Approve', mark: '✓', color: '#2e7d4f', bg: 'rgba(46,125,79,0.12)', key: 'a' },
  { status: 'not_approved', label: 'Not approved', mark: '✕', color: '#b03a2e', bg: 'rgba(176,58,46,0.10)', key: 'n' },
  { status: 'discuss', label: 'Change or discuss', mark: '?', color: '#b9770e', bg: 'rgba(185,119,14,0.12)', key: 'd' },
]
const BY = Object.fromEntries(CHOICES.map(c => [c.status, c]))
const imageOf = (it) => { const a = it.attrs || {}; return a['Image URL'] || a.image_url || null }
const isUrl = (v) => typeof v === 'string' && /^https?:\/\/\S+$/i.test(v.trim())
const SKIP = new Set(['Image URL', 'image_url', '_room', 'Rooms'])
function detailsOf(it) {
  const a = it.attrs || {}, rows = [], links = []
  for (const [k, v] of Object.entries(a)) {
    if (SKIP.has(k) || v == null || v === '' || typeof v === 'object') continue
    if (isUrl(String(v))) { links.push({ k, url: String(v).trim() }); continue }
    rows.push([k, String(v)])
  }
  return { rows, links }
}
const todayLA = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
const fmtDay = (ymd) => ymd ? new Date(ymd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''

export default function SelectionsWalkthrough() {
  const [msId, setMsId] = useState(null)
  useEffect(() => {
    const on = (e) => setMsId((e.detail && e.detail.msId) || null)
    window.addEventListener('open-selections-walkthrough', on)
    return () => window.removeEventListener('open-selections-walkthrough', on)
  }, [])
  if (!msId) return null
  return createPortal(<Walkthrough msId={msId} onClose={() => setMsId(null)} />, document.body)
}

export function Walkthrough({ msId, onClose, api }) {
  const svc = api || { loadSelectionsReview, recordSelectionAnswer, writeSelectionsToMinutes }
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [i, setI] = useState(0)
  const [who, setWho] = useState(null)        // { id, name } of the client speaking
  const [meetingId, setMeetingId] = useState('')
  const [note, setNote] = useState('')
  const [noteFor, setNoteFor] = useState(null) // status waiting on a note
  const [busy, setBusy] = useState(false)
  const [zoom, setZoom] = useState(false)
  const [done, setDone] = useState(null)       // minutes result

  useEffect(() => {
    svc.loadSelectionsReview(msId).then(d => {
      setData(d)
      if (d.people && d.people.length === 1) setWho(d.people[0])
      const today = (d.meetings || []).find(m => m.date === todayLA())
      if (today) setMeetingId(today.id)
      const firstOpen = (d.items || []).findIndex(it => !d.decisions[it.id])
      if (firstOpen > 0) setI(firstOpen)
    }).catch(e => setErr(e.message || 'Could not load the selections'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msId])

  const items = (data && data.items) || []
  const it = items[i]
  const meeting = data && (data.meetings || []).find(m => m.id === meetingId)
  const member = ctx.currentMember && ctx.currentMember.fields
  const answered = items.filter(x => data && data.decisions[x.id]).length

  const record = useCallback(async (status, n) => {
    if (!it || busy) return
    if (!who) { setErr('Pick who is giving the answer first.'); return }
    setBusy(true); setErr('')
    try {
      const r = await svc.recordSelectionAnswer({
        milestoneId: msId, entryId: data.entryId, item: { id: it.id, version: it.version, item_label: it.item_label, schedule_type: it.schedule_type },
        status, note: n || '', clientName: who.name, recipientId: who.id || null,
        meetingId: meetingId || null, meetingDate: meeting ? meeting.date : todayLA(),
        memberName: (member && (member['Member Name'] || member['Name'])) || null, memberId: ctx.currentMember ? ctx.currentMember.id : null,
      })
      setData(d => ({ ...d, decisions: { ...d.decisions, [it.id]: r.decision } }))
      setNote(''); setNoteFor(null)
      if (i < items.length - 1) setI(i + 1)
    } catch (e) { setErr(e.message || 'That answer did not save') }
    finally { setBusy(false) }
  }, [it, busy, who, msId, data, meetingId, meeting, member, i, items.length, svc])

  function choose(c) {
    if (c.status === 'approved') return record('approved', '')
    setNoteFor(c.status); setNote('')
  }

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) { if (e.key === 'Escape') { setNoteFor(null) } return }
      if (zoom && e.key === 'Escape') { setZoom(false); return }
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') setI(x => Math.min(items.length - 1, x + 1))
      else if (e.key === 'ArrowLeft') setI(x => Math.max(0, x - 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [items.length, zoom, onClose])

  async function toMinutes() {
    if (!meetingId) { setErr('Pick the meeting to write the answers into.'); return }
    setBusy(true); setErr('')
    try { setDone(await svc.writeSelectionsToMinutes({ milestoneId: msId, entryId: data.entryId, meetingId, meetingDate: meeting && meeting.date })) }
    catch (e) { setErr(e.message || 'Could not write the minutes') }
    finally { setBusy(false) }
  }

  const shell = { position: 'fixed', inset: 0, zIndex: 900, background: '#f4f7f8', display: 'flex', flexDirection: 'column', fontFamily: '"DM Sans",sans-serif', color: INK }
  const top = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 20px', background: INK, color: '#fff' }}>
      <div style={{ fontFamily: '"Montserrat",sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {data ? data.milestone.projectName : 'Selections'}
      </div>
      <div style={{ fontSize: 13, opacity: 0.8 }}>{data ? data.milestone.name : ''}</div>
      <div style={{ flex: 1 }} />
      {data && items.length > 0 && <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 12, opacity: 0.85 }}>{answered} of {items.length} answered</div>}
      <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>Close</button>
    </div>
  )

  if (!data) return <div style={shell}>{top}<div style={{ padding: 40, color: err ? '#b03a2e' : MUTED }}>{err || 'Loading the selections…'}</div></div>
  if (!data.entryId || !items.length) {
    return (
      <div style={shell}>{top}
        <div style={{ padding: 40, maxWidth: 560, lineHeight: 1.5 }}>
          {!data.entryId ? 'These selections are not on the Client Hub yet. Post the Selections Package and add it to the Client Hub, then walk them through it.'
            : 'There are no issued schedule items on this package to walk through.'}
        </div>
      </div>
    )
  }

  const dec = data.decisions[it.id]
  const cur = dec && BY[dec.status]
  const img = imageOf(it)
  const { rows, links } = detailsOf(it)
  const where = [it.room_string, it.spec_section].filter(Boolean)

  return (
    <div style={shell}>
      {top}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '10px 20px', borderBottom: '0.5px solid rgba(7,39,45,0.12)', background: '#fff', flexWrap: 'wrap', fontSize: 13 }}>
        <span style={{ color: MUTED }}>Answer given by</span>
        {(data.people || []).map(p => (
          <button key={p.id} onClick={() => setWho(p)}
            style={{ padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              border: '0.5px solid ' + (who && who.id === p.id ? INK : 'rgba(7,39,45,0.2)'), background: who && who.id === p.id ? INK : '#fff', color: who && who.id === p.id ? '#fff' : INK }}>{p.name}</button>
        ))}
        {!(data.people || []).length && <span style={{ color: '#b03a2e' }}>Nobody is on this project's Client Hub yet.</span>}
        <span style={{ width: 1, height: 20, background: 'rgba(7,39,45,0.15)' }} />
        <span style={{ color: MUTED }}>Meeting</span>
        <select value={meetingId} onChange={e => setMeetingId(e.target.value)} style={{ fontSize: 13, padding: '4px 6px', border: '0.5px solid rgba(7,39,45,0.25)', borderRadius: 4, fontFamily: 'inherit' }}>
          <option value="">Not in a scheduled meeting</option>
          {(data.meetings || []).map(m => <option key={m.id} value={m.id}>{m.type} · {fmtDay(m.date)}</option>)}
        </select>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ width: 260, flexShrink: 0, overflowY: 'auto', borderRight: '0.5px solid rgba(7,39,45,0.12)', background: '#fff' }}>
          {items.map((x, k) => {
            const d = data.decisions[x.id], c = d && BY[d.status]
            return (
              <button key={x.id} onClick={() => setI(k)}
                style={{ display: 'flex', gap: 8, width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', borderBottom: '0.5px solid rgba(7,39,45,0.06)', cursor: 'pointer',
                  background: k === i ? 'rgba(7,39,45,0.06)' : 'transparent', fontFamily: 'inherit', fontSize: 13, color: INK }}>
                <span style={{ width: 16, color: c ? c.color : MUTED, fontWeight: 700 }}>{c ? c.mark : '·'}</span>
                <span style={{ flex: 1 }}>{x.item_label}<span style={{ display: 'block', fontSize: 11, color: MUTED }}>{x.schedule_type}</span></span>
              </button>
            )
          })}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 40px' }}>
          <div style={{ display: 'flex', gap: 36, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {img && (
              <button onClick={() => setZoom(true)} title="See it larger"
                style={{ padding: 0, border: '0.5px solid rgba(7,39,45,0.15)', borderRadius: 10, overflow: 'hidden', background: '#fff', cursor: 'zoom-in', width: 'min(520px, 100%)', aspectRatio: '4 / 3' }}>
                <img src={img} alt={it.item_label || ''} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              </button>
            )}
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 12, color: MUTED, fontFamily: '"Montserrat",sans-serif', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{it.schedule_type} · {i + 1} of {items.length}</div>
              <div style={{ fontSize: 28, fontWeight: 600, margin: '6px 0 4px' }}>{it.item_label}</div>
              {where.length > 0 && <div style={{ fontSize: 14, color: MUTED, marginBottom: 14 }}>{where.join(' · ')}</div>}
              {rows.length > 0 && (
                <table style={{ borderCollapse: 'collapse', fontSize: 14, marginBottom: 12 }}>
                  <tbody>{rows.map(([k, v]) => (
                    <tr key={k}><td style={{ color: MUTED, padding: '3px 16px 3px 0', verticalAlign: 'top' }}>{k}</td><td style={{ padding: '3px 0' }}>{v}</td></tr>
                  ))}</tbody>
                </table>
              )}
              {links.length > 0 && (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
                  {links.map(l => <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2b6c8a', fontSize: 14 }}>{/link/i.test(l.k) ? 'Product page' : l.k} ↗</a>)}
                </div>
              )}
              {dec && (
                <div style={{ fontSize: 13, color: cur ? cur.color : MUTED, margin: '6px 0 14px' }}>
                  {cur ? cur.mark + ' ' + cur.label : dec.status} · {dec.decided_by_name}{dec.decided_at ? ', ' + new Date(dec.decided_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  {dec.source === 'meeting' && dec.recorded_by_name ? <span style={{ color: MUTED }}> · in our meeting, recorded by {dec.recorded_by_name}</span> : dec.source === 'hub' ? <span style={{ color: MUTED }}> · on the Client Hub</span> : null}
                  {dec.note && <div style={{ color: INK, fontStyle: 'italic', marginTop: 3 }}>&ldquo;{dec.note}&rdquo;</div>}
                </div>
              )}

              {noteFor ? (
                <div style={{ maxWidth: 520 }}>
                  <textarea value={note} onChange={e => setNote(e.target.value)} autoFocus
                    placeholder={noteFor === 'discuss' ? 'What would they like changed, or talked through?' : 'What is not right about it? (optional)'}
                    style={{ width: '100%', minHeight: 80, padding: 10, borderRadius: 8, border: '0.5px solid rgba(7,39,45,0.25)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button disabled={busy || (noteFor === 'discuss' && !note.trim())} onClick={() => record(noteFor, note.trim())}
                      style={{ padding: '9px 18px', borderRadius: 20, border: 'none', background: INK, color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: busy || (noteFor === 'discuss' && !note.trim()) ? 0.4 : 1 }}>
                      Record {BY[noteFor].label.toLowerCase()}
                    </button>
                    <button onClick={() => setNoteFor(null)} style={{ padding: '9px 14px', borderRadius: 20, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {CHOICES.map(c => (
                    <button key={c.status} disabled={busy} onClick={() => choose(c)}
                      style={{ padding: '11px 20px', borderRadius: 24, cursor: 'pointer', fontSize: 15, fontWeight: 600,
                        border: '0.5px solid ' + c.color, background: cur && cur.status === c.status ? c.bg : '#fff', color: c.color }}>
                      {c.mark} {c.label}
                    </button>
                  ))}
                </div>
              )}
              {err && <div style={{ color: '#b03a2e', fontSize: 13, marginTop: 10 }}>{err}</div>}

              <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
                <button disabled={i === 0} onClick={() => setI(i - 1)} style={{ padding: '7px 14px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', cursor: 'pointer', opacity: i === 0 ? 0.4 : 1 }}>← Previous</button>
                <button disabled={i === items.length - 1} onClick={() => setI(i + 1)} style={{ padding: '7px 14px', borderRadius: 4, border: '0.5px solid rgba(7,39,45,0.25)', background: '#fff', cursor: 'pointer', opacity: i === items.length - 1 ? 0.4 : 1 }}>Next →</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', background: '#fff', borderTop: '0.5px solid rgba(7,39,45,0.12)', fontSize: 13 }}>
        {done ? (
          <span style={{ color: '#2e7d4f' }}>Written into the {meeting ? meeting.type + ' minutes of ' + fmtDay(meeting.date) : 'meeting minutes'}: {done.lines} answer{done.lines === 1 ? '' : 's'} ({done.approved} approved). The client can correct the record when the minutes are posted.</span>
        ) : (
          <span style={{ color: MUTED }}>Anything not answered here, the client finishes on their own on the Client Hub.</span>
        )}
        <div style={{ flex: 1 }} />
        <button disabled={busy || !meetingId} onClick={toMinutes} title={meetingId ? '' : 'Pick the meeting first'}
          style={{ padding: '9px 18px', borderRadius: 20, border: 'none', background: INK, color: '#fff', fontWeight: 600, cursor: meetingId ? 'pointer' : 'default', opacity: busy || !meetingId ? 0.4 : 1 }}>
          Write the answers into the minutes
        </button>
      </div>

      {zoom && img && (
        <div onClick={() => setZoom(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(7,39,45,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
          <img src={img} alt={it.item_label || ''} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6, background: '#fff' }} />
        </div>
      )}
    </div>
  )
}
